

import { useState } from "react";
import SearchableDropdown from "../components/SearchableDropdown";

interface InvoiceFormData {
  client: string;
  event: string;
  total: string;
  discount: string;
  advance: string;
  status: string;
  date: string;
  dueDate: string;
  notes: string;
}

interface InvoiceFormProps {
  onSubmit: (data: InvoiceFormData) => void;
  onCancel: () => void;
  initialData?: Partial<InvoiceFormData>;
}

const statuses = ["pending", "partial", "paid"];
const eventsList = [
  { id: "EVT-001", client: "Mehta Family" },
  { id: "EVT-002", client: "Sharma Corp" },
  { id: "EVT-003", client: "Patel Family" },
  { id: "EVT-004", client: "Desai Wedding" },
  { id: "EVT-005", client: "Tech Summit 2026" },
  { id: "EVT-006", client: "Kumar Anniversary" },
];

export default function InvoiceForm({ onSubmit, onCancel, initialData }: InvoiceFormProps) {
  const [formData, setFormData] = useState<InvoiceFormData>({
    client: initialData?.client || "",
    event: initialData?.event || "",
    total: initialData?.total || "",
    discount: initialData?.discount || "0",
    advance: initialData?.advance || "0",
    status: initialData?.status || "pending",
    date: initialData?.date || (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; })(),
    dueDate: initialData?.dueDate || "",
    notes: initialData?.notes || "",
  });

  const [errors, setErrors] = useState<Partial<Record<keyof InvoiceFormData, string>>>({});

  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof InvoiceFormData, string>> = {};
    
    if (!formData.client.trim()) newErrors.client = "Client name is required";
    if (!formData.event) newErrors.event = "Event is required";
    if (!formData.total || parseFloat(formData.total) <= 0) newErrors.total = "Valid total amount is required";
    if (!formData.dueDate) newErrors.dueDate = "Due date is required";
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      onSubmit(formData);
    }
  };

  const handleChange = (field: keyof InvoiceFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const calculateFinal = () => {
    const total = parseFloat(formData.total) || 0;
    const discount = parseFloat(formData.discount) || 0;
    return total - discount;
  };

  const calculateBalance = () => {
    return calculateFinal() - (parseFloat(formData.advance) || 0);
  };

  const inputClass = (field: keyof InvoiceFormData) => `
    w-full px-3 py-2.5 rounded-xl border text-sm transition-colors
    ${errors[field] 
      ? "border-rose-300 bg-rose-50 focus:border-rose-400 focus:ring-2 focus:ring-rose-100" 
      : "border-stone-200 bg-white focus:border-amber-400 focus:ring-2 focus:ring-amber-100"
    }
    outline-none
  `;

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* Client Name */}
        <div className="sm:col-span-2">
          <label className="block text-xs font-medium text-stone-500 mb-1.5">Client Name *</label>
          <input
            type="text"
            value={formData.client}
            onChange={(e) => handleChange("client", e.target.value)}
            placeholder="Enter client name"
            className={inputClass("client")}
          />
          {errors.client && <p className="text-xs text-rose-500 mt-1">{errors.client}</p>}
        </div>

        {/* Event Selection */}
        <div className="sm:col-span-2">
          <SearchableDropdown
            label="Select Event *"
            options={eventsList.map(ev => ({ value: ev.id, label: `${ev.id} - ${ev.client}` }))}
            value={formData.event}
            onChange={(value) => {
              const selected = eventsList.find(ev => ev.id === value);
              handleChange("event", value);
              if (selected) handleChange("client", selected.client);
            }}
            placeholder="Search and select an event"
            error={errors.event}
          />
        </div>

        {/* Total Amount */}
        <div>
          <label className="block text-xs font-medium text-stone-500 mb-1.5">Total Amount (₹) *</label>
          <input
            type="number"
            min="0"
            step="0.01"
            value={formData.total}
            onChange={(e) => handleChange("total", e.target.value)}
            placeholder="0.00"
            className={inputClass("total")}
          />
          {errors.total && <p className="text-xs text-rose-500 mt-1">{errors.total}</p>}
        </div>

        {/* Discount */}
        <div>
          <label className="block text-xs font-medium text-stone-500 mb-1.5">Discount (₹)</label>
          <input
            type="number"
            min="0"
            step="0.01"
            value={formData.discount}
            onChange={(e) => handleChange("discount", e.target.value)}
            placeholder="0.00"
            className={inputClass("discount")}
          />
        </div>

        {/* Advance Payment */}
        <div>
          <label className="block text-xs font-medium text-stone-500 mb-1.5">Advance Paid (₹)</label>
          <input
            type="number"
            min="0"
            step="0.01"
            value={formData.advance}
            onChange={(e) => handleChange("advance", e.target.value)}
            placeholder="0.00"
            className={inputClass("advance")}
          />
        </div>

        {/* Status */}
        <div>
          <label className="block text-xs font-medium text-stone-500 mb-1.5">Payment Status</label>
          <select
            value={formData.status}
            onChange={(e) => handleChange("status", e.target.value)}
            className={inputClass("status")}
          >
            {statuses.map(s => (
              <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
            ))}
          </select>
        </div>

        {/* Invoice Date */}
        <div>
          <label className="block text-xs font-medium text-stone-500 mb-1.5">Invoice Date</label>
          <input
            type="date"
            value={formData.date}
            onChange={(e) => handleChange("date", e.target.value)}
            className={inputClass("date")}
          />
        </div>

        {/* Due Date */}
        <div>
          <label className="block text-xs font-medium text-stone-500 mb-1.5">Due Date *</label>
          <input
            type="date"
            value={formData.dueDate}
            onChange={(e) => handleChange("dueDate", e.target.value)}
            className={inputClass("dueDate")}
          />
          {errors.dueDate && <p className="text-xs text-rose-500 mt-1">{errors.dueDate}</p>}
        </div>

        {/* Summary */}
        <div className="sm:col-span-2 bg-stone-50 rounded-xl p-4">
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <p className="text-xs text-stone-400">Final Amount</p>
              <p className="text-lg font-bold text-stone-800">₹{calculateFinal().toLocaleString('en-IN')}</p>
            </div>
            <div>
              <p className="text-xs text-stone-400">Advance</p>
              <p className="text-lg font-bold text-emerald-600">₹{(parseFloat(formData.advance) || 0).toLocaleString('en-IN')}</p>
            </div>
            <div>
              <p className="text-xs text-stone-400">Balance</p>
              <p className={`text-lg font-bold ${calculateBalance() > 0 ? 'text-amber-600' : 'text-emerald-600'}`}>
                ₹{calculateBalance().toLocaleString('en-IN')}
              </p>
            </div>
          </div>
        </div>

        {/* Notes */}
        <div className="sm:col-span-2">
          <label className="block text-xs font-medium text-stone-500 mb-1.5">Notes</label>
          <textarea
            value={formData.notes}
            onChange={(e) => handleChange("notes", e.target.value)}
            placeholder="Additional notes or payment terms..."
            rows={3}
            className={`${inputClass("notes")} resize-none`}
          />
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-3 pt-4 border-t border-stone-100">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 px-4 py-2.5 rounded-xl bg-stone-100 text-stone-600 font-semibold text-sm hover:bg-stone-200 transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="flex-1 px-4 py-2.5 rounded-xl text-white font-semibold text-sm gold-gradient hover:opacity-90 transition-opacity"
        >
          {initialData ? "Update Invoice" : "Create Invoice"}
        </button>
      </div>
    </form>
  );
}
